/**
 * Ring PRO checkout line items with bundle attributes.
 * One UUID per bundle; ring line gets _bundle_id + _is_bundle_parent; all other lines get _bundle_id.
 */
import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import { ClueShopifyMap } from 'content/products/addons/clue';
import {
  RingProVariants,
  RingProChargerCaseVariants,
  RingSizes,
  RingType,
  RingProColors, // eslint-disable-line @typescript-eslint/no-unused-vars
  RingProChargerCaseColor, // eslint-disable-line @typescript-eslint/no-unused-vars
} from 'content/products/ring-pro';
import { RingProSizingKitShopifyMap } from 'content/products/ring';
import { UhxShopifyMap } from 'content/products/uhx';
import { getTradeInCredit } from 'content/products/tradeIn';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import {
  AddonsAction,
  AddonsState,
  CheckoutParamsRingPro,
  initialAddonsStateRingPro,
} from './types';

export { initialAddonsStateRingPro };

export function addonsReducerRingPro(
  state: AddonsState,
  action: AddonsAction,
): AddonsState {
  switch (action.type) {
    case 'SET_ENGRAVING':
      return {
        ...state,
        engraving: { ...state.engraving, enabled: action.payload },
      };
    case 'SET_ENGRAVING_MESSAGE':
      return {
        ...state,
        engraving: { ...state.engraving, message: action.payload },
      };
    case 'SET_UHX_CART':
      return { ...state, uhxCart: action.payload };
    case 'SET_AFIB_CART':
      return { ...state, afibCart: action.payload };
    case 'SET_CARDIO_ADAPTIBILITY_CART':
      return { ...state, cardioAdaptibilityCart: action.payload };
    case 'SET_CNO_PRO_CART':
      return { ...state, cnoProCart: action.payload };
    case 'SET_CLUE_CART':
      return { ...state, clueCart: action.payload };
    case 'SET_RESPIRATORY_CART':
      return { ...state, respiratoryCart: action.payload };
    case 'SET_TRADE_IN_DEVICE':
      return {
        ...state,
        tradeIn: { ...state.tradeIn, device: action.payload },
      };
    case 'SET_TRADE_IN_SERIAL_NUMBER':
      return {
        ...state,
        tradeIn: { ...state.tradeIn, serialNumber: action.payload },
      };
    default:
      return state;
  }
}

function getEngravingKey(engraving: boolean): RingType {
  return engraving ? RingType.ENGRAVED : RingType.NOT_ENGRAVED;
}

/**
 * Builds checkout line items for Ring PRO with a single bundle ID for the whole flow.
 * Parent (ring) line has _bundle_id + _is_bundle_parent; charger and add-ons have _bundle_id only.
 */
export function getCheckoutLineItemsRingPro(
  params: CheckoutParamsRingPro,
): addToCartArgsType[] {
  const {
    color,
    size,
    store,
    chargerCaseColor,
    engraving,
    engravingMessage,
    eSizingSize,
    uhxCart,
    afibCart,
    cardioAdaptibilityCart,
    cnoProCart,
    clueCart,
    isCnOPlus,
    tradeInDevice,
    tradeInSerialNumber,
  } = params;

  if (!color || !size || !store) return [];

  const productNode = RingProVariants[color]?.[size];
  if (!productNode) return [];

  const mainVariantId = productNode[getEngravingKey(engraving)]?.variantId[store];
  if (!mainVariantId) return [];

  const hasUhx = Boolean(uhxCart);
  const noteAttributes: Array<{ key: string; value: string }> = [];
  if (engraving && engravingMessage)
    noteAttributes.push({ key: 'engraving', value: engravingMessage });
  if (eSizingSize) noteAttributes.push({ key: 'eSizing', value: 'true' });
  if (tradeInDevice) {
    const tradeInCredit = getTradeInCredit(tradeInDevice, store, hasUhx);
    noteAttributes.push({ key: 'trade_in_device', value: tradeInDevice });
    noteAttributes.push({
      key: 'trade_in_credit',
      value: String(tradeInCredit),
    });
    noteAttributes.push({
      key: 'trade_in_serial_number',
      value: tradeInSerialNumber,
    });
  }

  const lineItems: addToCartArgsType[] = [];

  // Sizing kit if OPEN size (child of bundle)
  if (size === RingSizes.OPEN && RingProSizingKitShopifyMap[store]?.shopifyVariantId) {
    lineItems.push({
      shopifyVariantId: RingProSizingKitShopifyMap[store].shopifyVariantId as string,
      productType: ProductType.RING,
    });
  }

  // Charging case (gold or silver, same size as ring) — child
  // Affiliate discount code is attached here so both codes reach Shopify
  const chargerVariantId = RingProChargerCaseVariants[chargerCaseColor]?.[
    size
  ]?.variantId[store];
  if (chargerVariantId) {
    lineItems.push({
      shopifyVariantId: chargerVariantId,
      productType: ProductType.RING,
      discountCode: params.affiliateDiscountCode,    });
  }

  // UHX — child
  if (uhxCart && UhxShopifyMap[store]?.[uhxCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: UhxShopifyMap[store][uhxCart].variantId as string,
      productType: ProductType.GENERIC,    });
  }

  // Cardio — child
  if (
    cardioAdaptibilityCart &&
    CardioAdaptibilityShopifyMap[store]?.[cardioAdaptibilityCart]?.variantId
  ) {
    lineItems.push({
      shopifyVariantId: CardioAdaptibilityShopifyMap[store][
        cardioAdaptibilityCart
      ].variantId as string,
      productType: ProductType.RING,    });
  }

  // Afib — child
  if (afibCart && AfibShopifyMap[store]?.[afibCart]?.variantId) {
    lineItems.push({
      shopifyVariantId: AfibShopifyMap[store][afibCart].variantId as string,
      productType: ProductType.RING,    });
  }

  // CNO Pro/Plus — child
  if (cnoProCart) {
    const cnoMap = isCnOPlus ? CnoPlusShopifyMap : CnoProShopifyMap;
    if (cnoMap[store]?.[cnoProCart]?.variantId) {
      lineItems.push({
        shopifyVariantId: cnoMap[store][cnoProCart].variantId as string,
        productType: ProductType.RING,      });
    }
  }

  // Clue — child
  if (clueCart === 'Year1' && ClueShopifyMap[store]?.Year1?.variantId) {
    lineItems.push({
      shopifyVariantId: ClueShopifyMap[store].Year1.variantId as string,
      productType: ProductType.RING,    });
  }

  // DISABLED: Respiratory Health PowerPlug — re-enable by uncommenting
  // // Respiratory — child
  // if (
  //   respiratoryCart === 'Year1' &&
  //   RespiratoryShopifyMap[store]?.Year1?.variantId
  // ) {
  //   lineItems.push({
  //     shopifyVariantId: RespiratoryShopifyMap[store].Year1.variantId as string,
  //     productType: ProductType.RING,
  //     params: { noteAttributes: bundleAttrs },
  //   });
  // }

  // Ring PRO (parent) — last, with _is_bundle_parent
  lineItems.push({
    shopifyVariantId: mainVariantId,
    productType: ProductType.RING_PRO,
    discountCode: params.serverDiscount?.code,
    params: {
      productType: 'ring_pro',
      noteAttributes,
    },
  });

  return tagBundle(lineItems);
}
